import { HOST, LOGIN_API_HOST } from '@/config';
import { RTSState } from '@/store/slices/rts';
import { STSState } from '@/store/slices/sts';

const BASEURL = '/meeting_vertc_demo/login';

// 申请获取验证码入参
interface ISendLoginSms {
  cell_phone: string; // 用户手机号
  country_code?: string; // 国家/地区 编号
}

// 校验验证码入参
interface IVerifyLoginSms {
  cell_phone: string; // 用户手机号
  country_code?: string; // 国家/地区 编号
  code: string;
}

// 校验验证码成功时后端返回内容
export interface IUserInfo {
  user_id: string; // 校验成功时, 将 map 用户手机号至唯一的 用户 id
  user_name: string; // 用户名称
  login_token: string; // 业务服务器登陆token
  created_at: number; // 创建时间
}

// 校验验证码成功时出参
export interface ILoginReturn {
  code: number; // 成功返回情况下, 将返回 200
  message: string; // 成功返回情况下, 将返回 'ok', 失败情况下将返回失败原因
  response: IUserInfo;
}

interface IGetSTS {
  file_name: string;
  login_token: string;
}

// 加入 RTS 房间入参
interface IJoinRTS {
  login_token: string;
  // scenes_name: SceneType;
}

/**
 * 输入用户手机号, 申请获取验证码
 * @param body
 * @returns
 */
export const sendLoginSms = (body: ISendLoginSms): void => {
  fetch(`${HOST}${BASEURL}`, {
    method: 'POST',
    body: JSON.stringify({
      event_name: 'sendLoginSms',
      content: JSON.stringify({
        ...body,
        country_code: body.country_code || '86',
      }),
    }),
  });
};

/**
 * 发起业务服务器登录请求 - 验证码登录
 * 普通场景下, 用户需要先在登录页面通过验证码进行校验
 * 非 prod 环境, 需要把验证码发送给后端, 如果后端判定验证码合法, 则用户成功登录
 * 可以获得业务服务器返回的 user_id 和 login_token
 * @param body
 * @returns
 */
export const verifyLoginSms = (
  body: IVerifyLoginSms
): Promise<{
  code: number; // 成功返回情况下, 将返回 200
  message: string; // 成功返回情况下, 将返回 'ok', 失败情况下将返回失败原因
  response: IUserInfo;
}> => {
  return fetch(`${HOST}${BASEURL}`, {
    method: 'POST',
    body: JSON.stringify({
      event_name: 'verifyLoginSms',
      content: JSON.stringify({
        ...body,
        country_code: body.country_code || '86',
      }),
    }),
  }).then((res) => {
    return res.json();
  });
};

/**
 * 发起业务服务器登录请求 - 验证码登录(无参数登录)
 * 普通场景下, 用户需要先在登录页面通过验证码进行校验
 * 在 prod 环境下, 需要用到账号中台提供的 accountSDK, 调用 accountSDK.login API 申请验证码并且进行校验
 * 校验成功后, accountSDK.login API 将 resolve, 之后登录业务服务器就不需要提供入参
 * 可以获得业务服务器返回的 user_id 和 login_token
 * @param body
 * @returns
 */
export const login = (): Promise<{
  code: number;
  message: string;
  response: IUserInfo;
}> => {
  return fetch(`${LOGIN_API_HOST}${BASEURL}`, {
    method: 'POST',
    body: JSON.stringify({
      event_name: 'Login',
      content: JSON.stringify({}),
    }),
  }).then((res) => {
    return res.json();
  });
};

/**
 * 发起业务服务器请求 - 免密登录
 * 主要用于云录屏场景
 * 云录屏场景下没有登录页面，也不需要输入验证码, 用户通过在 url 指定 userId 和 role 识别出当前是云录屏,
 * 免密登录并且获得业务服务器返回的 user_id 和 login_token
 * @param body
 * @returns
 */
export const freeLoginApi = (body: {
  device_id: string;
  user_name: string;
}): Promise<{
  code: number;
  message: string;
  response: IUserInfo;
}> => {
  return fetch(`${HOST}${BASEURL}`, {
    method: 'POST',
    body: JSON.stringify({
      event_name: 'passwordFreeLogin',
      device_id: body.device_id,
      content: JSON.stringify({
        user_name: body.user_name,
      }),
    }),
  }).then((res) => {
    return res.json();
  });
};

/**
 * 获取临时 Account（Access Key ID） / SK(Secret Access Key) / STS（Security Token Service）Token
 * 用于云存储文件, 每个文件都需要专门的临时 AK / SK / STSToken
 * @param body
 * @returns
 */
export const getSTS = (
  body: IGetSTS
): Promise<{
  code: number;
  loginToken: string;
  response: STSState;
}> => {
  return fetch(`${HOST}${BASEURL}`, {
    method: 'POST',
    body: JSON.stringify({
      event_name: 'sts',
      content: JSON.stringify(body),
    }),
  }).then((res) => {
    return res.json();
  });
};

/**
 * 获取加入rts房间必要的参数
 * @param body
 * @returns
 */
export const getRTSParams = (
  body: IJoinRTS
): Promise<{
  code: number;
  message: string;
  response: RTSState;
}> => {
  return fetch(`${HOST}${BASEURL}`, {
    method: 'POST',
    body: JSON.stringify({
      event_name: 'joinRTS',
      content: JSON.stringify(body),
    }),
  }).then((res) => {
    return res.json();
  });
};

/**
 * 更新用户名
 * @param body
 * @returns
 */
export const changeUserName = (body: {
  user_name: string;
  login_token: string;
}): Promise<{
  code: number;
  message: string;
}> => {
  return fetch(`${HOST}${BASEURL}`, {
    method: 'POST',
    body: JSON.stringify({
      event_name: 'changeUserName',
      content: JSON.stringify(body),
    }),
  }).then((res) => {
    return res.json();
  });
};
